import { CargoOwnerOption } from "components/bid/form/config";
import { defaultMultipleSelectProps, OptionWithFederalSubject, ValueContainer } from "components/shared/select";
import { waybillRegistryStatuses } from "components/waybills/status";
import {
    bidCalculationStatusesEnum,
    bidPaymentStatuses,
    bidPointLoadUnloadStatuses,
    bidStatusesEnum,
    distributionBidStatusesEnum,
    syncEntityStatuses,
} from "helpers/enums";
import compact from "lodash/compact";
import React from "react";
import { selectList as counterpartiesList } from "sources/books/cargoOwners";
import { namesListWithDeleted as driversList } from "sources/books/drivers";
import { list as logistsList } from "sources/books/employees";
import { list as legalPersonsList } from "sources/books/legalPersons";
import { list as trailersList } from "sources/books/trailers";
import { carNumbersWithBids as trucksList } from "sources/books/trucks";
import { getRegionsByName } from "sources/map";

export const bidStatusOptions = [
    { label: "Черновик", value: "drafts" },
    { label: "Активная", value: "active" },
    { label: "Запланирована", value: "planned" },
    { label: "Выполнена", value: "done" },
    { label: "Не учтена", value: "nocoordinates" },
    { label: "Отменена", value: "canceled" },
];

export const bidFilterValues = {
    estimations: {
        firstLatePeriod: { ne: null },
        status: { in: [bidStatusesEnum.Started] },
    },
    status: {
        drafts: [bidStatusesEnum.New],
        active: [bidStatusesEnum.Started],
        planned: [bidStatusesEnum.Planned],
        done: [bidStatusesEnum.Done],
        canceled: [bidStatusesEnum.Canceled],
        nocoordinates: [bidStatusesEnum.Started],
    },
    statusExtendFilter: {
        nocoordinates: {
            calculationStatus: {
                in: [
                    bidCalculationStatusesEnum.NoCoordinates,
                    bidCalculationStatusesEnum.NoActivity,
                    bidCalculationStatusesEnum.HasPlanMileageDeviation,
                ],
            },
        },
    },
};

export const distributionBidFilterValues = {
    status: {
        withBid: { bidId: { ne: null } },
        withoutBid: {
            bidId: null,
            hiredCarCompletedAt: null,
            isDeleted: false,
            status: { ne: distributionBidStatusesEnum.Canceled },
        },
        completedBid: { hiredCarCompletedAt: { ne: null } },
        cancellationBid: { status: distributionBidStatusesEnum.Canceled },
    },
};

const statusConfig = {
    ...defaultMultipleSelectProps,
    placeholder: "Статус",
    inputProps: { name: "status" },
    options: bidStatusOptions,
    unSearchable: true,
    components: { ValueContainer },
};

const paymentStatusConfig = {
    ...defaultMultipleSelectProps,
    placeholder: "Статус оплаты",
    inputProps: { name: "payment/paymentStatus" },
    options: bidPaymentStatuses,
    unSearchable: true,
    components: { ValueContainer },
};

const syncStatusConfig = {
    ...defaultMultipleSelectProps,
    placeholder: "Статус синхронизации",
    inputProps: { name: "lastSyncStatus" },
    options: syncEntityStatuses,
    unSearchable: true,
    components: { ValueContainer },
};

const loadStatusConfig = {
    ...defaultMultipleSelectProps,
    placeholder: "Статус загрузки",
    inputProps: { name: "bidPointLoadUnloadStatus" },
    options: bidPointLoadUnloadStatuses,
    unSearchable: true,
    components: { ValueContainer },
};

const distributionBidsStatusConfig = {
    ...defaultMultipleSelectProps,
    placeholder: "Статус",
    inputProps: { name: "distributionBidsStatus" },
    options: [
        { label: "Выполнена", value: "completedBid" },
        { label: "Без заявки", value: "withoutBid" },
        { label: "Создана заявка", value: "withBid" },
        { label: "Отменен", value: "cancellationBid" },
    ],
    unSearchable: true,
    components: { ValueContainer },
};

const distributionBidCreatorConfig = {
    ...defaultMultipleSelectProps,
    placeholder: (
        <div className="ellipsis" title="Автор заказа">
            Автор заказа
        </div>
    ),
    inputProps: { name: "createdById" },
    propName: "fullNameOrEmail",
    url: logistsList,
    components: { ValueContainer },
};

const operatorStatusConfig = {
    ...defaultMultipleSelectProps,
    isMulti: false,
    placeholder: "Статус",
    inputProps: { name: "approveStatus" },
    options: [
        { label: "Согласована", value: "true" },
        { label: "Не согласована", value: "false" },
    ],
    unSearchable: true,
    components: { ValueContainer },
};

const acceptedByDriverConfig = {
    ...defaultMultipleSelectProps,
    isMulti: false,
    placeholder: "Принята водителем",
    inputProps: { name: "acceptedByDriver" },
    options: [
        { label: "Принята водителем", value: "true" },
        { label: "Не принята водителем", value: "false" },
    ],
};

const hasUploadedFileConfig = {
    ...defaultMultipleSelectProps,
    placeholder: "Заявки с файлами",
    inputProps: { name: "hasUploadedFile" },
    components: { ValueContainer },
    options: [
        { label: "В чате заявки", value: "hasChatUploadedFile" },
        { label: "В чате с водителем", value: "hasDriverChatUploadedFile" },
        { label: "Без файлов в чате заявки", value: "withoutChatUploadedFile" },
        { label: "Без файлов в чате с водителем", value: "withoutDriverChatUploadedFile" },
    ],
};

const logistConfig = {
    ...defaultMultipleSelectProps,
    placeholder: (
        <div className="ellipsis" title="Логист, ответственный или менеджер по продажам">
            Логист, ответственный или менеджер по продажам
        </div>
    ),
    inputProps: { name: "logistId" },
    propName: "fullNameOrEmail",
    url: logistsList,
    components: { ValueContainer },
};

const carLogistConfig = {
    ...defaultMultipleSelectProps,
    placeholder: (
        <div className="ellipsis" title="Логист">
            Логист
        </div>
    ),
    inputProps: { name: "carLogistId" },
    propName: "fullNameOrEmail",
    url: logistsList,
    components: { ValueContainer },
};

const responsibleConfig = {
    ...defaultMultipleSelectProps,
    placeholder: (
        <div className="ellipsis" title="Ответственный">
            Ответственный
        </div>
    ),
    inputProps: { name: "responsibleId" },
    propName: "fullNameOrEmail",
    url: logistsList,
    components: { ValueContainer },
};

const salesManagerConfig = {
    ...defaultMultipleSelectProps,
    placeholder: (
        <div className="ellipsis" title="Менеджер по продажам">
            Менеджер по продажам
        </div>
    ),
    inputProps: { name: "salesManagerId" },
    propName: "fullNameOrEmail",
    url: logistsList,
    components: { ValueContainer },
};

const counterpartyConfig = {
    ...defaultMultipleSelectProps,
    placeholder: "Контрагент",
    inputProps: { name: "counterpartyId" },
    propName: ({ name, inn, kpp }) => (inn || kpp ? `${name} (${compact([inn, kpp]).join("/")})` : name),
    url: counterpartiesList,
    components: { Option: CargoOwnerOption, ValueContainer },
    menuPortalTarget: document.body,
    filterPropName: ["name", "inn", "kpp"],
};

const cargoSenderCounterpartyConfig = {
    ...counterpartyConfig,
    placeholder: "Грузоотправитель",
    inputProps: { name: "loadPointCounterpartyId" },
};

const cargoReceiverCounterpartyConfig = {
    ...counterpartyConfig,
    placeholder: "Грузополучатель",
    inputProps: { name: "unloadPointCounterpartyId" },
};

const hiredCarCounterpartyConfig = {
    ...counterpartyConfig,
    placeholder: "Перевозчик",
    inputProps: { name: "hiredCarCounterpartyId" },
};

const carConfig = {
    ...defaultMultipleSelectProps,
    placeholder: (
        <div className="ellipsis" title="Номер машины или колонна">
            Номер машины или колонна
        </div>
    ),
    inputProps: { name: "car" },
    propName: "number",
    hasGroups: true,
    url: trucksList,
};

const trailerIdConfig = {
    ...defaultMultipleSelectProps,
    placeholder: "Прицеп",
    inputProps: { name: "trailerId" },
    propName: "number",
    hasGroups: true,
    url: trailersList,
};

const trailerConfig = {
    ...defaultMultipleSelectProps,
    placeholder: "Тип прицепа",
    inputProps: { name: "trailerTypeId" },
    propName: "trailerTypeId",
    hasGroups: true,
    menuPortalTarget: document.body,
};

const legalPersonConfig = {
    ...defaultMultipleSelectProps,
    placeholder: "Организация",
    inputProps: { name: "legalPersonId" },
    propName: "name",
    url: legalPersonsList,
    components: { ValueContainer },
};

const driverConfig = {
    ...defaultMultipleSelectProps,
    placeholder: "Водитель",
    inputProps: { name: "driverId" },
    filterPropName: "fullName",
    propName: (item) => (item.isDeleted ? item.fullName + " (Удалён)" : item.fullName),
    url: driversList,
    components: { ValueContainer },
};

const fromPointConfig = (value, withRadius = false) => ({
    ...defaultMultipleSelectProps,
    value,
    placeholder: "Откуда, область, город",
    inputProps: { name: "from" },
    url: getRegionsByName,
    propName: "name",
    minimumInputLength: 2,
    components: { Option: OptionWithFederalSubject, ValueContainer },
    styles: {
        container: (styles, { isFocused }) => ({
            ...styles,
            zIndex: isFocused ? 2 : 0,
            width: value && withRadius ? "calc(100% - 76px)" : "100%",
            display: value && withRadius ? "inline-block" : "block",
        }),
        control: (styles) => ({
            ...styles,
            minHeight: "30.5px",
            fontSize: "14px",
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            borderTopRightRadius: value && withRadius ? "0" : "4px",
            "&:hover": {
                zIndex: 1,
            },
        }),
        input: (styles) => ({ ...styles, height: "17px", paddingTop: 0, marginTop: 0 }),
        singleValue: (styles) => ({ ...styles, height: "18px", lineHeight: "18px" }),
        option: (styles) => ({ ...styles, fontSize: "14px" }),
        valueContainer: (styles) => ({
            ...styles,
            lineHeight: "18px",
            paddingLeft: "29px",
            position: "relative",
            "&:before": {
                fontSize: "10px",
                fontWeight: "600",
                lineHeight: "13px",
                content: '"A"',
                position: "absolute",
                left: "7px",
                display: "block",
                borderRadius: "50%",
                border: "1px solid",
                width: "15px",
                height: "15px",
                textAlign: "center",
            },
        }),
        indicatorsContainer: (styles) => ({ ...styles, height: "30.5px" }),
        dropdownIndicator: (styles) => ({ ...styles, padding: "4px", display: "none" }),
        indicatorSeparator: () => ({ display: "none" }),
        placeholder: (styles) => ({ ...styles, lineHeight: "18px", whiteSpace: "nowrap" }),
    },
});

const toPointConfig = (value, withRadius = false) => ({
    ...defaultMultipleSelectProps,
    value,
    placeholder: "Куда, область, город",
    inputProps: { name: "to" },
    url: getRegionsByName,
    propName: "name",
    minimumInputLength: 2,
    components: { Option: OptionWithFederalSubject, ValueContainer },
    styles: {
        container: (styles, { isFocused }) => ({
            ...styles,
            zIndex: isFocused ? 2 : 0,
            marginTop: "-4.5px",
            width: value && withRadius ? "calc(100% - 76px)" : "100%",
            display: value && withRadius ? "inline-block" : "block",
        }),
        control: (styles) => ({
            ...styles,
            minHeight: "30.5px",
            fontSize: "14px",
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            borderBottomRightRadius: value && withRadius ? "0" : "4px",
            "&:hover": {
                zIndex: 1,
            },
        }),
        input: (styles) => ({ ...styles, height: "17px", paddingTop: 0, marginTop: 0 }),
        singleValue: (styles) => ({ ...styles, height: "18px", lineHeight: "18px" }),
        option: (styles) => ({ ...styles, fontSize: "14px" }),
        valueContainer: (styles) => ({
            ...styles,
            lineHeight: "18px",
            paddingLeft: "29px",
            position: "relative",
            "&:before": {
                fontSize: "10px",
                fontWeight: "600",
                lineHeight: "13px",
                content: '"B"',
                position: "absolute",
                left: "7px",
                display: "block",
                borderRadius: "50%",
                border: "1px solid",
                width: "15px",
                height: "15px",
                textAlign: "center",
            },
        }),
        indicatorsContainer: (styles) => ({ ...styles, height: "30.5px" }),
        dropdownIndicator: (styles) => ({ ...styles, padding: "4px", display: "none" }),
        indicatorSeparator: () => ({ display: "none" }),
        placeholder: (styles) => ({ ...styles, lineHeight: "18px", whiteSpace: "nowrap" }),
    },
});

const loadDateConfig = {
    from: {
        inPortal: true,
        wrapper: "date-filter",
        inputProps: {
            name: "loadFrom",
            type: "datetime",
        },
        libOptions: {
            className: "b-filter__input b-filter__input--tall b-filter__input--border-radius-top-left",
            placeholderText: "Дата загрузки, от",
            popperPlacement: "top-start",
            name: "loadFrom",
            isClearable: true,
        },
    },
    to: {
        inPortal: true,
        wrapper: "date-filter",
        inputProps: {
            name: "loadBefore",
            type: "datetime",
        },
        libOptions: {
            className: "b-filter__input b-filter__input--tall b-filter__input--border-radius-top-right",
            placeholderText: "До",
            popperPlacement: "top-start",
            name: "loadBefore",
            isClearable: true,
        },
    },
};

const unloadDateConfig = {
    from: {
        inPortal: true,
        wrapper: "date-filter",
        inputProps: {
            name: "unloadFrom",
            type: "datetime",
        },
        libOptions: {
            className: "b-filter__input b-filter__input--tall b-filter__input--border-radius-bottom-left",
            placeholderText: "Дата выгрузки, от",
            popperPlacement: "top-start",
            name: "unloadFrom",
            isClearable: true,
        },
    },
    to: {
        inPortal: true,
        wrapper: "date-filter",
        inputProps: {
            name: "unloadBefore",
            type: "datetime",
        },
        libOptions: {
            className: "b-filter__input b-filter__input--tall b-filter__input--border-radius-bottom-right",
            placeholderText: "До",
            popperPlacement: "top-start",
            name: "unloadBefore",
            isClearable: true,
        },
    },
};

const waybillStatusConfig = {
    ...defaultMultipleSelectProps,
    placeholder: "Статус ЭТрН",
    inputProps: { name: "waybillStatus" },
    options: waybillRegistryStatuses.map(({ label, value }) => ({ label, value })),
    menuPlacement: "top",
    unSearchable: true,
    components: { ValueContainer },
};

const waybillSignatureRequiredConfig = {
    ...defaultMultipleSelectProps,
    isMulti: false,
    placeholder: "Потребность в подписи ЭТрН",
    inputProps: { name: "waybillSignatureRequired" },
    options: [
        { label: "Требует подписания", value: "true" },
        { label: "Не требует подписания", value: "false" },
    ],
    unSearchable: true,
    components: { ValueContainer },
};

export {
    trailerConfig,
    statusConfig,
    paymentStatusConfig,
    loadStatusConfig,
    logistConfig,
    carConfig,
    driverConfig,
    trailerIdConfig,
    fromPointConfig,
    toPointConfig,
    loadDateConfig,
    unloadDateConfig,
    operatorStatusConfig,
    acceptedByDriverConfig,
    counterpartyConfig,
    cargoSenderCounterpartyConfig,
    cargoReceiverCounterpartyConfig,
    hiredCarCounterpartyConfig,
    distributionBidsStatusConfig,
    distributionBidCreatorConfig,
    syncStatusConfig,
    legalPersonConfig,
    hasUploadedFileConfig,
    responsibleConfig,
    salesManagerConfig,
    carLogistConfig,
    waybillStatusConfig,
    waybillSignatureRequiredConfig,
};
